/**
 * Word 文档导出器
 * 严格复刻 demo/ExportPanel.tsx 的 exportWordDocument 逻辑
 */

import type { SchemaInput, NumFormat } from '../schema/core/types';
import { computeLevelStyle } from '../schema/core/styleResolver';
import { halfPointsToPt } from '../schema/utils/units';
import { ensureLevelInheritance } from '../schema/core/schemaManager';

// ============ Word A4 页面常量 ============

/** A4 页面宽度 (twips) */
const PAGE_WIDTH = 11906;
/** A4 页面高度 (twips) */
const PAGE_HEIGHT = 16838;
/** 默认左右边距 (twips) - 1 inch */
const MARGIN_LEFT = 1440;
const MARGIN_RIGHT = 1440;
/** 默认上下边距 (twips) */
const MARGIN_TOP = 1440;
const MARGIN_BOTTOM = 1440;
/** 内容可用宽度 (twips) = 11906 - 1440 - 1440 = 9026 */
const CONTENT_WIDTH_TWIPS = PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT;

// ============ 类型定义 ============

/** 结构化内容项 */
export interface StructuredContentItem {
  level: number; // 1-based level (1, 2, 3, 4...)
  content: string;
}

/** 导出选项 */
export interface ExportOptions {
  /** 使用的 AbstractNum ID */
  abstractNumId: number;
  /** 文档标题（可选） */
  title?: string;
  /** 最大级别（默认 8，即 0-8 共9级） */
  maxLevel?: number;
  /** 继承源级别（默认 4）- Level 5-8会继承此级别的格式 */
  inheritFromLevel?: number;
}

// ============ 核心导出函数（复刻 demo/ExportPanel.tsx） ============

/**
 * 导出为 Word 文档并下载
 * 严格按照 demo ExportPanel.tsx 的 exportWordDocument 逻辑
 */
async function exportWordDocument(
  content: StructuredContentItem[],
  schema: SchemaInput,
  abstractNumId: number,
  title?: string,
  filename = `document_${Date.now()}.docx`
): Promise<void> {
  // 动态导入 docx 库
  const docx = await import('docx');
  const { Document, Packer, Paragraph, TextRun, AlignmentType, LevelFormat } = docx;

  const abstractKey = `AbstractNum_${abstractNumId}`;
  const abstractNum = schema.numberingSchemas[abstractKey];

  if (!abstractNum) {
    throw new Error(`${abstractKey} not found in schema`);
  }

  // NumFormat 映射
  const numFmtMap: Record<string, any> = {
    decimal: LevelFormat.DECIMAL,
    lowerLetter: LevelFormat.LOWER_LETTER,
    upperLetter: LevelFormat.UPPER_LETTER,
    lowerRoman: LevelFormat.LOWER_ROMAN,
    upperRoman: LevelFormat.UPPER_ROMAN,
    chineseCountingThousand: LevelFormat.CHINESE_COUNTING_THOUSAND,
    bullet: LevelFormat.BULLET,
  };

  // 生成段落样式 - 每个级别对应一个段落样式
  const paragraphStyles: any[] = [];

  for (let i = 0; i < Math.min(9, abstractNum.levels.length); i++) {
    const levelDef = abstractNum.levels[i];
    const computed = computeLevelStyle(levelDef, schema.paragraphStyles);

    // 构建段落样式的字符格式
    const runStyle: any = {
      size: computed.fontSize ? halfPointsToPt(computed.fontSize) * 2 : undefined,
      bold: computed.bold,
      italics: computed.italic,
      underline: computed.underline ? {} : undefined,
    };

    // 设置字体
    if (computed.font) {
      runStyle.font = {
        ascii: computed.font.ascii || undefined,
        eastAsia: computed.font.eastAsia || undefined,
        hAnsi: computed.font.hAnsi || computed.font.ascii || undefined,
        cs: computed.font.eastAsia || undefined,
      };
    }

    // 创建段落样式
    const paraStyle: any = {
      id: `ListLevel${i}_${abstractNumId}`,
      name: `List Level ${i} (Num ${abstractNumId})`,
      basedOn: 'Normal',
      run: runStyle,
      paragraph: {
        spacing: computed.spacing ? {
          before: computed.spacing.before ? parseInt(computed.spacing.before, 10) : undefined,
          after: computed.spacing.after ? parseInt(computed.spacing.after, 10) : undefined,
          line: computed.spacing.line ? parseInt(computed.spacing.line, 10) : undefined,
          lineRule: computed.spacing.lineRule || undefined,
        } : undefined,
        numbering: {
          reference: `numbering-${abstractNumId}`,
          level: i,
        },
      },
    };

    paragraphStyles.push(paraStyle);
  }

  // 生成 numbering 配置 - 导出所有9个级别（0-8）
  const levels = [];
  for (let i = 0; i < Math.min(9, abstractNum.levels.length); i++) {
    const levelDef = abstractNum.levels[i];
    const computed = computeLevelStyle(levelDef, schema.paragraphStyles);
    const isNoneType = !computed.pattern;

    // 确保缩进不超出内容区域
    const clampedLeft = Math.min(computed.indent.left, CONTENT_WIDTH_TWIPS - 200);
    const clampedHanging = isNoneType ? 0 : Math.min(computed.indent.hanging, clampedLeft);

    // 构建级别配置
    const levelConfig: any = {
      level: i,
      format: isNoneType ? LevelFormat.DECIMAL : (numFmtMap[computed.numFmt] || LevelFormat.DECIMAL),
      text: isNoneType ? '' : computed.pattern.replace(/%(\d)/g, `%${i + 1}`),
      start: computed.start,
      style: {
        paragraph: {
          indent: {
            left: clampedLeft,
            hanging: clampedHanging,
          },
        },
        run: {
          // 编号本身的格式（项目符号的格式）
          font: computed.font ? {
            ascii: computed.font.ascii || undefined,
            eastAsia: computed.font.eastAsia || undefined,
            hAnsi: computed.font.hAnsi || computed.font.ascii || undefined,
            cs: computed.font.eastAsia || undefined,
          } : undefined,
          size: computed.fontSize ? halfPointsToPt(computed.fontSize) * 2 : undefined,
          bold: computed.bold,
          italics: computed.italic,
        },
      },
    };

    levels.push(levelConfig);
  }

  // 创建段落
  const paragraphs: any[] = [];

  // 标题
  if (title) {
    paragraphs.push(
      new Paragraph({
        children: [new TextRun({ text: title, bold: true, size: 32 })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 },
      })
    );
  }

  // 内容
  for (const item of content) {
    const levelIndex = item.level - 1;
    const levelDef = abstractNum.levels[levelIndex];

    if (!levelDef) {
      paragraphs.push(new Paragraph({ children: [new TextRun(item.content)] }));
      continue;
    }

    // 构建 TextRun 配置 - 文本本身不需要设置格式，由段落样式控制
    const textRunOptions: any = {
      text: item.content,
    };

    // 构建段落配置 - 应用对应级别的段落样式
    const paragraphOptions: any = {
      children: [new TextRun(textRunOptions)],
      style: `ListLevel${levelIndex}_${abstractNumId}`, // 应用段落样式
    };

    paragraphs.push(new Paragraph(paragraphOptions));
  }

  // 创建文档 - 包含段落样式定义
  // 覆盖 Normal 默认样式，清除 Word 内置的段后间距，避免与前端预览不一致
  const doc = new Document({
    numbering: {
      config: [{
        reference: `numbering-${abstractNumId}`,
        levels,
      }],
    },
    styles: {
      default: {
        document: {
          run: {},
          paragraph: {
            spacing: { before: 0, after: 0, line: 240 },
          },
        },
      },
      paragraphStyles: paragraphStyles,
    },
    sections: [{
      properties: {
        page: {
          size: { width: PAGE_WIDTH, height: PAGE_HEIGHT },
          margin: { top: MARGIN_TOP, bottom: MARGIN_BOTTOM, left: MARGIN_LEFT, right: MARGIN_RIGHT },
        },
      },
      children: paragraphs,
    }],
  });

  // 生成并下载
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ============ 对外接口 ============

/**
 * 导出为 Word 文档（返回 Blob）
 */
export async function exportToWord(
  content: StructuredContentItem[],
  schema: SchemaInput,
  options: ExportOptions
): Promise<Blob> {
  const { abstractNumId, maxLevel = 8, inheritFromLevel = 4 } = options;
  const updatedSchema = ensureLevelInheritance(schema, abstractNumId, inheritFromLevel);

  const docx = await import('docx');
  const { Document, Packer, Paragraph, TextRun, AlignmentType, LevelFormat } = docx;

  const abstractKey = `AbstractNum_${abstractNumId}`;
  const abstractNum = updatedSchema.numberingSchemas[abstractKey];
  if (!abstractNum) throw new Error(`${abstractKey} not found in schema`);

  const numFmtMap: Record<string, any> = {
    decimal: LevelFormat.DECIMAL,
    lowerLetter: LevelFormat.LOWER_LETTER,
    upperLetter: LevelFormat.UPPER_LETTER,
    lowerRoman: LevelFormat.LOWER_ROMAN,
    upperRoman: LevelFormat.UPPER_ROMAN,
    chineseCountingThousand: LevelFormat.CHINESE_COUNTING_THOUSAND,
    bullet: LevelFormat.BULLET,
  };

  const paragraphStyles: any[] = [];
  for (let i = 0; i < Math.min(9, abstractNum.levels.length); i++) {
    const levelDef = abstractNum.levels[i];
    const computed = computeLevelStyle(levelDef, updatedSchema.paragraphStyles);
    const runStyle: any = {
      size: computed.fontSize ? halfPointsToPt(computed.fontSize) * 2 : undefined,
      bold: computed.bold,
      italics: computed.italic,
      underline: computed.underline ? {} : undefined,
    };
    if (computed.font) {
      runStyle.font = {
        ascii: computed.font.ascii || undefined,
        eastAsia: computed.font.eastAsia || undefined,
        hAnsi: computed.font.hAnsi || computed.font.ascii || undefined,
        cs: computed.font.eastAsia || undefined,
      };
    }
    paragraphStyles.push({
      id: `ListLevel${i}_${abstractNumId}`,
      name: `List Level ${i} (Num ${abstractNumId})`,
      basedOn: 'Normal',
      run: runStyle,
      paragraph: {
        spacing: computed.spacing ? {
          before: computed.spacing.before ? parseInt(computed.spacing.before, 10) : undefined,
          after: computed.spacing.after ? parseInt(computed.spacing.after, 10) : undefined,
          line: computed.spacing.line ? parseInt(computed.spacing.line, 10) : undefined,
          lineRule: computed.spacing.lineRule || undefined,
        } : undefined,
        numbering: { reference: `numbering-${abstractNumId}`, level: i },
      },
    });
  }

  const levels: any[] = [];
  for (let i = 0; i < Math.min(9, abstractNum.levels.length); i++) {
    const levelDef = abstractNum.levels[i];
    const computed = computeLevelStyle(levelDef, updatedSchema.paragraphStyles);
    const isNoneType = !computed.pattern;
    const clampedLeft2 = Math.min(computed.indent.left, CONTENT_WIDTH_TWIPS - 200);
    const clampedHanging2 = isNoneType ? 0 : Math.min(computed.indent.hanging, clampedLeft2);
    levels.push({
      level: i,
      format: isNoneType ? LevelFormat.DECIMAL : (numFmtMap[computed.numFmt] || LevelFormat.DECIMAL),
      text: isNoneType ? '' : computed.pattern.replace(/%(\d)/g, `%${i + 1}`),
      start: computed.start,
      style: {
        paragraph: {
          indent: {
            left: clampedLeft2,
            hanging: clampedHanging2,
          },
        },
        run: {
          font: computed.font ? {
            ascii: computed.font.ascii || undefined,
            eastAsia: computed.font.eastAsia || undefined,
            hAnsi: computed.font.hAnsi || computed.font.ascii || undefined,
            cs: computed.font.eastAsia || undefined,
          } : undefined,
          size: computed.fontSize ? halfPointsToPt(computed.fontSize) * 2 : undefined,
          bold: computed.bold,
          italics: computed.italic,
        },
      },
    });
  }

  const paragraphs: any[] = [];
  if (options.title) {
    paragraphs.push(
      new Paragraph({
        children: [new TextRun({ text: options.title, bold: true, size: 32 })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 },
      })
    );
  }
  for (const item of content) {
    const levelIndex = item.level - 1;
    if (!abstractNum.levels[levelIndex]) {
      paragraphs.push(new Paragraph({ children: [new TextRun(item.content)] }));
      continue;
    }
    paragraphs.push(new Paragraph({
      children: [new TextRun({ text: item.content })],
      style: `ListLevel${levelIndex}_${abstractNumId}`,
    }));
  }

  const doc = new Document({
    numbering: { config: [{ reference: `numbering-${abstractNumId}`, levels }] },
    styles: {
      default: {
        document: {
          run: {},
          paragraph: {
            spacing: { before: 0, after: 0, line: 240 },
          },
        },
      },
      paragraphStyles,
    },
    sections: [{
      properties: {
        page: {
          size: { width: PAGE_WIDTH, height: PAGE_HEIGHT },
          margin: { top: MARGIN_TOP, bottom: MARGIN_BOTTOM, left: MARGIN_LEFT, right: MARGIN_RIGHT },
        },
      },
      children: paragraphs,
    }],
  });

  return await Packer.toBlob(doc);
}

/**
 * 下载 Word 文档
 */
export async function downloadWord(
  content: StructuredContentItem[],
  schema: SchemaInput,
  options: ExportOptions,
  filename = 'document.docx'
): Promise<void> {
  const { abstractNumId, inheritFromLevel = 4 } = options;
  const updatedSchema = ensureLevelInheritance(schema, abstractNumId, inheritFromLevel);

  await exportWordDocument(content, updatedSchema, abstractNumId, options.title, filename);
}

/**
 * 导出 Schema 为 JSON 并下载
 */
export function downloadSchemaJSON(
  schema: SchemaInput,
  filename = 'schema.json'
): void {
  const json = JSON.stringify(schema, null, 2);
  const blob = new Blob([json], { type: 'application/json' });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
